import { useCallback, useEffect, useRef } from 'react';

// A2 (LEA-379) — Cloudflare Turnstile for the Prism funnel, INVISIBLE + execute-
// on-demand. Unlike the signup `useTurnstile` (a *visible* widget whose token
// arrives via an async callback), the paid-ad funnel must stay frictionless AND
// be able to mint a fresh token right before each protected call (/analyze,
// /search). So this renders one invisible, `interaction-only` widget and exposes
// `getToken()` → `turnstile.execute()`.
//
// KILL-SWITCH / FAIL-OPEN (PRISM-SPECIFIC since LEA-587):
//   Disabled unless VITE_TURNSTILE_ENABLED === 'true' AND a site key is set —
//   then `getToken()` resolves '' and the PRISM endpoints tolerate the
//   tokenless call (the backend passes allowMissing at their call sites —
//   deliberate LEA-379 A2 funnel decision). getToken() also resolves '' on any
//   widget error or an 8s timeout, so a stuck or broken challenge can never
//   block the funnel — only Cloudflare actively scoring the visitor as a bot
//   rejects a call. NOTE: this tolerance is NOT the backend default anymore —
//   signup/DSR fail CLOSED on a missing token when the gate is armed (LEA-587),
//   so do not copy this pattern to a new surface without an explicit decision.

const TURNSTILE_ENABLED = import.meta.env.VITE_TURNSTILE_ENABLED === 'true';
const TURNSTILE_SITE_KEY = import.meta.env.VITE_CLOUDFLARE_TURNSTILE_SITE_KEY;
// Reuse signup's script id so a single <script> tag is shared across the app.
const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
const TURNSTILE_SCRIPT_URL =
  import.meta.env.VITE_CLOUDFLARE_TURNSTILE_SCRIPT_URL ||
  'https://challenges.cloudflare.com/turnstile/v0/api.js';
// Hidden container the invisible widget mounts into — PrismShell renders this div.
export const PRISM_TURNSTILE_CONTAINER_ID = 'cf-turnstile-prism';
const EXECUTE_TIMEOUT_MS = 8000;

interface UsePrismTurnstileReturn {
  /** True only when Turnstile is enabled AND a site key is configured. */
  enabled: boolean;
  /** Mint a fresh single-use token for the next protected call. Resolves '' when
   *  disabled or on any error/timeout (the prism endpoints tolerate that via
   *  allowMissing — prism-only, see header comment). */
  getToken: () => Promise<string>;
}

export function usePrismTurnstile(): UsePrismTurnstileReturn {
  const widgetId = useRef<string | null>(null);
  // Resolver for the currently-executing challenge, set per getToken() call.
  const resolveRef = useRef<((token: string) => void) | null>(null);
  const enabled = TURNSTILE_ENABLED && !!TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!enabled) return;

    const renderWidget = () => {
      const turnstile = (window as unknown as { turnstile?: TurnstileGlobal }).turnstile;
      if (!turnstile) return;
      const container = document.getElementById(PRISM_TURNSTILE_CONTAINER_ID);
      if (!container || container.childElementCount > 0) return; // guard StrictMode double-mount
      widgetId.current = turnstile.render(`#${PRISM_TURNSTILE_CONTAINER_ID}`, {
        sitekey: TURNSTILE_SITE_KEY as string,
        size: 'invisible',
        appearance: 'interaction-only',
        execution: 'execute',
        callback: (t: string) => {
          resolveRef.current?.(t);
          resolveRef.current = null;
        },
        'error-callback': () => {
          resolveRef.current?.('');
          resolveRef.current = null;
        },
        'expired-callback': () => {
          resolveRef.current?.('');
          resolveRef.current = null;
        },
      });
    };

    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (!existingScript) {
      const script = document.createElement('script');
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.onload = renderWidget;
      document.head.appendChild(script);
    } else {
      renderWidget();
    }

    return () => {
      const turnstile = (window as unknown as { turnstile?: TurnstileGlobal }).turnstile;
      if (widgetId.current && turnstile) {
        turnstile.remove(widgetId.current);
        widgetId.current = null;
      }
    };
  }, [enabled]);

  const getToken = useCallback((): Promise<string> => {
    const turnstile = (window as unknown as { turnstile?: TurnstileGlobal }).turnstile;
    if (!enabled || !turnstile || !widgetId.current) return Promise.resolve('');
    const id = widgetId.current;
    return new Promise<string>((resolve) => {
      let settled = false;
      const done = (t: string) => {
        if (settled) return;
        settled = true;
        resolveRef.current = null;
        // Re-arm the widget so the NEXT getToken() mints a fresh single-use token.
        try {
          turnstile.reset(id);
        } catch {
          // ignore — reset is best-effort
        }
        resolve(t);
      };
      resolveRef.current = done;
      // Fail-open timeout — never block the funnel on a stuck challenge.
      setTimeout(() => done(''), EXECUTE_TIMEOUT_MS);
      try {
        turnstile.execute(id);
      } catch {
        done('');
      }
    });
  }, [enabled]);

  return { enabled, getToken };
}

// Minimal subset of Cloudflare's Turnstile global (extends the signup hook's copy
// with `execute` + the invisible render options).
interface TurnstileGlobal {
  render: (
    selector: string,
    options: {
      sitekey: string;
      size?: 'invisible' | 'normal' | 'compact' | 'flexible';
      appearance?: 'always' | 'execute' | 'interaction-only';
      execution?: 'render' | 'execute';
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
    },
  ) => string;
  execute: (widgetId: string, options?: Record<string, unknown>) => void;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}
